import { SyncStatus, SYNC_STATUS_POLL_INTERVAL_MS } from 'constants/common';
import { useEffect, useState } from 'react';
import { api } from 'services/api';

type SyncType = 'tour' | 'transportation' | 'accommodation' | 'supplier';

interface ISyncStatusProps {
  syncType: SyncType;
}

interface SyncStatusRow {
  status: SyncStatus;
}

const SYNC_FUNCTION_NAMES: Record<SyncType, string> = {
  accommodation: 'SYNC_ACCOMMODATIONS',
  supplier: 'SYNC_SUPPLIERS',
  tour: 'SYNC_TOURS',
  transportation: 'SYNC_TRANSPORTATIONS'
};

const MESSAGE_KEYS: Record<SyncStatus, string> = {
  PENDING: 'sync.warningStatus.pending',
  RUNNING: 'sync.warningStatus.running',
  COMPLETED: 'sync.warningStatus.completed',
  FAILED: 'sync.warningStatus.failed'
};

// The banner shows the most significant status among the latest runs
const STATUS_PRIORITY = [
  SyncStatus.RUNNING,
  SyncStatus.PENDING,
  SyncStatus.COMPLETED,
  SyncStatus.FAILED
];

const LATEST_RUNS = 20;

/** Polls the latest runs of one sync function and exposes the banner message. */
export const useSyncStatus = ({ syncType }: ISyncStatusProps) => {
  const [syncStatusData, setSyncStatusData] = useState<{
    syncType: SyncType;
    messageKey: string;
  }>();

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const { data } = await api.get<{ data: SyncStatusRow[] }>(
          '/admin/sync-statuses',
          {
            params: {
              functionName: SYNC_FUNCTION_NAMES[syncType],
              pageSize: LATEST_RUNS,
              sortBy: JSON.stringify({ id: 'desc' })
            }
          }
        );
        if (!active) return;

        const found = STATUS_PRIORITY.find(status =>
          data.some(run => run.status === status)
        );
        setSyncStatusData({
          syncType,
          messageKey: found ? MESSAGE_KEYS[found] : ''
        });
      } catch {
        // keep the last known banner; the next poll retries
      }
    };

    load();
    const timer = setInterval(load, SYNC_STATUS_POLL_INTERVAL_MS);

    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [syncType]);

  return {
    syncStatusData
  };
};
